import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { BillingStrategy } from '@galaxy/billing';
import { EnvironmentService } from '@galaxy/core';
import {
  GetMultiOrderFormsResponseOrderFormContainerInterface as MPOrderFormContainerInterface,
  MarketplaceAppsApiService,
} from '@galaxy/marketplace-apps/v1';
import { TranslateService } from '@ngx-translate/core';
import { BlamePackageActivation } from '@vendasta/accounts/legacy';
import { ProductActivationPrereqFormComponent } from '@vendasta/businesses';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  FulfillmentOrderConfigApiService,
  GetFulfillmentOrderConfigRequest,
  ReleaseType,
} from '@vendasta/marketplace-apps';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Field, LineItem, Order } from '@vendasta/sales-orders';
import { SalesOrderFormComponent } from '@vendasta/sales-ui';
import {
  CommonFormData,
  CustomFieldsAnswers,
  IncludedCommonFormFieldsInterface,
  OrderFormFieldOptionsType,
  OrderFormInterface,
  OrderFormOptionsInterface,
  ProductInfoInterface,
  ResellerItem,
} from '@vendasta/store';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  forkJoin as observableForkJoin,
  Observable,
  of as observableOf,
  of,
  Subscription,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  publishReplay,
  refCount,
  shareReplay,
  skipWhile,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AppConfigService } from '../../../app-config.service';
import { ButtonDescriptor, VDialogComponent } from '../../../common/v-dialog.component';
import { AccountService } from '../../../core/account.service';
import { BillingApiService } from '../../../core/billing/billing-api.service';
import { PricingService } from '../../../core/billing/pricing.service';
import { PurchaseCost } from '../../../core/billing/purchase-cost';
import { HidePricingGuard } from '../../../core/guards/hide-pricing-guard.service';
import { getResellerItemUniqueId } from '../../../core/reseller';
import { ResellerItemService } from '../../../core/reseller/reseller-item.service';
import { BillingStrategy as DeprecatedStrategy } from '../../../core/subscription-level';
import { User } from '../../../core/user';
import { godaddyAppId, whitelabelDomainAppId } from '../../../marketplace-app/global-brands-constants';
import { OrderConfirmationItem } from '../../confirmation-page/interface';
import { OrderConfirmationService } from '../../confirmation-page/order-confirmation.service';
import { StairstepOrderService } from '../../confirmation-page/stairstep-order.service';
import { ActivationNavigationService } from '../../core/activation-navigation.service';
import { ActivationParamService } from '../../core/activation-param.service';
import { ActivationService } from '../../core/activation.service';
import { AppIdToEditionIdToEditionMap, OrderFormType } from '../../core/interface';
import { ProductOrderFormStoreService } from '../../core/product-order-form.store.service';
import { FILE_UPLOAD_URL, ORDER_FORM_OPTIONS } from '../constants';
import { ResellerItemOrderForm, StatusMessage } from '../interface';
import { ProductOrderFormService } from '../product-order-form.service';
import { filterStatusMessagesInOrder, sortStatusMessages } from '../utils';

type OrderFormSubmissionErrorType = 'activationFailure' | 'unknownFailure' | 'noAppsError' | 'invalidForm';

interface OrderFormSubmissionError {
  appNames: string[];
  type: OrderFormSubmissionErrorType;
  errorMessage: string;
  openFailureDialog: boolean;
}

interface OrderData {
  orderForms: OrderFormInterface[];
  commonData: CommonFormData;
  answers: CustomFieldsAnswers[];
  options: OrderFormOptionsInterface;
  users: User[];
}

@Component({
  styleUrls: ['order-form-page.component.scss'],
  templateUrl: 'order-form-page.component.html',
  providers: [ProductOrderFormService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrderFormPageComponent implements OnInit, OnDestroy {
  @ViewChild('salesOrderForm') salesOrderFormComponent: SalesOrderFormComponent;
  @ViewChild('productActivationPrereqForm')
  productActivationPrereqFormComponent: ProductActivationPrereqFormComponent;

  form: UntypedFormGroup = new UntypedFormGroup({});
  includedApps$: Observable<ResellerItem[]>;
  includedAddons$: Observable<ResellerItem[]>;
  mpOrderForms$: Observable<MPOrderFormContainerInterface[]>;
  fileUploadUrl = FILE_UPLOAD_URL;
  productInfo: ProductInfoInterface[];
  options: OrderFormOptionsInterface;
  orderData$: Observable<OrderData>;
  isFormLoading = false;

  // checkoutItems are used to handle billing and activation restriction logic, and to find productInfo above
  checkoutItems: ResellerItemOrderForm[];
  addonCheckoutItems: ResellerItemOrderForm[];
  appCheckoutItems: ResellerItemOrderForm[];

  vDialog: MatDialogRef<VDialogComponent>;
  hasItemsThatAreNotFree: boolean;
  accountGroupId$: Observable<string>;
  accountGroupId = '';

  partnerId$: Observable<string>;
  partnerId = '';
  marketId$: Observable<string>;
  marketId = '';

  activationsRequiresApproval: string[];
  subscriptions: Subscription[] = [];

  // TODO: Rely solely on these
  appQuantityMap$: Observable<Map<string, number>>;
  addonQuantityMap$: Observable<Map<string, number>>;

  // verbose apps are all of the apps you are buying and the parent apps of any addons being bought
  verboseAppQuantityMap: Map<string, number>;
  orderFormAndPrice$: Observable<{ orderForm: ResellerItemOrderForm; price: PurchaseCost }[]>;
  purchaseCosts: Map<string, any> = new Map();
  hidePricing = true;
  hasInstantBillItems = false;
  hasContactSale: boolean;
  skus: string[];
  instantBillItemsSkus: string[];
  contactSalesSkus: string[];
  packageInfo$: Observable<BlamePackageActivation>;
  statusMessages$: Observable<StatusMessage[]>;

  lineItems$: Observable<LineItem[]>;
  salesOrder$: Observable<Order>;
  productIds$: Observable<string[]> = of([]);

  numCustomFormFields = 0;
  shouldDisplayBanner$: Observable<boolean>;
  accountName$: Observable<string>;
  resellerName$: Observable<string>;

  //Order confirmation attributes
  orderConfirmationItems$: Observable<OrderConfirmationItem[]>;
  productIsInstantBilledMap$: Observable<Map<string, BillingStrategy>>;
  userCanEditBillingInfo$: Observable<boolean>;
  billingInfoConfigured$: Observable<boolean>;
  stairstepOrderRequiresBilling$: Observable<boolean>;
  editionsMap$: Observable<AppIdToEditionIdToEditionMap>;
  salesOrderCached$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  someProductHasWorkOrderEnabled$: Observable<boolean>;

  constructor(
    private formService: ProductOrderFormService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private resellerItemService: ResellerItemService,
    private accountService: AccountService,
    private hidePricingGuard: HidePricingGuard,
    private billingApiService: BillingApiService,
    private app: AppConfigService,
    private pricingService: PricingService,
    private orderParamService: ActivationParamService,
    private orderFormStoreService: ProductOrderFormStoreService,
    private activationService: ActivationService,
    private activationNavigationService: ActivationNavigationService,
    private environmentService: EnvironmentService,
    private appService: MarketplaceAppsApiService,
    private translate: TranslateService,
    private productAnalyticsService: ProductAnalyticsService,
    private productConfirmationService: OrderConfirmationService,
    private stairstepOrderService: StairstepOrderService,
    private formConfigService: FulfillmentOrderConfigApiService,
  ) {}

  private filterForAddons(addons: ResellerItem[], addonQuantityMap: Map<string, number>): ResellerItem[] {
    return addons.filter((addon) => {
      if (addonQuantityMap.has(addon.addonId)) {
        if (!this.verboseAppQuantityMap.has(addon.appId)) {
          this.verboseAppQuantityMap.set(addon.appId, 0);
        }
        addon.quantity = addonQuantityMap.get(addon.addonId);
        return true;
      }
      return false;
    });
  }

  // Verbose apps are all of the apps you are buying and the parent apps of any addons being bought
  private filterForVerboseApps(apps: ResellerItem[]): ResellerItem[] {
    return this.filterForAppsGivenMap(apps, this.verboseAppQuantityMap);
  }

  private filterForAppsGivenMap(apps: ResellerItem[], verboseAppQuantityMap: Map<string, number>): ResellerItem[] {
    return apps.filter((app) => {
      if (verboseAppQuantityMap.has(app.appId)) {
        app.quantity = verboseAppQuantityMap.get(app.appId);
        return true;
      }
      return false;
    });
  }

  // tslint:disable:no-string-literal
  ngOnInit(): void {
    // skip to next step if no items require order form
    this.subscriptions.push(
      this.activationService.selectedItemsRequireOrderForm$
        .pipe(first())
        .subscribe((requiresOrderForm: OrderFormType) => {
          if (requiresOrderForm === OrderFormType.NONE) {
            this.activationNavigationService.navigateToNextStep('order-form');
          }
        }),
    );
    this.accountGroupId$ = this.orderParamService.accountGroupId$;
    this.marketId$ = this.orderParamService.marketId$;
    this.partnerId$ = this.orderParamService.partnerId$;

    //Order form confirmation attributes
    this.orderConfirmationItems$ = this.productConfirmationService.orderConfirmationItems$;
    this.productIsInstantBilledMap$ = this.productConfirmationService.productIsInstantBilledMap$;
    this.userCanEditBillingInfo$ = this.productConfirmationService.userCanEditBillingInfo$;
    this.billingInfoConfigured$ = this.productConfirmationService.billingInfoConfigured$;
    this.editionsMap$ = this.productConfirmationService.editionIdToEditionMap$;
    this.stairstepOrderRequiresBilling$ = this.stairstepOrderService.orderRequiresBillingInfo(
      this.orderConfirmationItems$,
    );

    this.subscriptions.push(
      this.accountGroupId$.subscribe((agid) => {
        this.accountService.setCurrentAccountID(agid, false);
        this.accountGroupId = agid;
      }),
    );

    this.subscriptions.push(
      this.partnerId$.subscribe((pid) => {
        this.partnerId = pid;
      }),
    );

    this.subscriptions.push(
      this.marketId$.subscribe((mid) => {
        this.marketId = mid;
      }),
    );

    // grabs app and addon quantity and ids from the url query params
    const resellerItemQuantityMap$ = this.orderParamService.resellerItemQuantityMapFromUrl$.pipe(shareReplay(1));

    // splits the reseller items into separate app and addon quantity mappings
    this.appQuantityMap$ = resellerItemQuantityMap$.pipe(
      map((quantityMap) => {
        const appQuantityMap = new Map<string, number>();

        Array.from(quantityMap.values()).forEach((itemQuantity) => {
          if (itemQuantity.appId && !itemQuantity.addonId) {
            appQuantityMap.set(getResellerItemUniqueId(itemQuantity), itemQuantity.quantity || 1);
          }
        });

        return appQuantityMap;
      }),
      shareReplay(1),
    );

    this.addonQuantityMap$ = resellerItemQuantityMap$.pipe(
      map((quantityMap) => {
        const addonQuantityMap = new Map<string, number>();

        Array.from(quantityMap.values()).forEach((itemQuantity) => {
          if (itemQuantity.addonId) {
            addonQuantityMap.set(getResellerItemUniqueId(itemQuantity), itemQuantity.quantity || 1);
          }
        });

        return addonQuantityMap;
      }),
      shareReplay(1),
    );

    const statuses$ = this.accountGroupId$.pipe(
      distinctUntilChanged(),
      switchMap((accountGroupId) => {
        return this.accountService.getAppStatusesForBusiness(accountGroupId);
      }),
      shareReplay(1),
    );

    this.statusMessages$ = combineLatest([
      this.resellerItemService.getAddonData(),
      this.resellerItemService.getAppsForSelectedMarket(),
      this.resellerItemService.getCustomProductData(),
      statuses$,
      resellerItemQuantityMap$,
    ]).pipe(
      map(([addons, apps, cps, statuses, resellerItemMap]) =>
        filterStatusMessagesInOrder(addons, apps, cps, statuses, resellerItemMap),
      ),
      map(sortStatusMessages),
    );

    this.salesOrder$ = this.orderFormStoreService.cachedOrder$;

    const env = this.environmentService.getEnvironment();

    const dynamicFieldValues$ = this.appQuantityMap$.pipe(
      switchMap((items) => {
        if (items.has(godaddyAppId[env])) {
          return this.orderFormStoreService.getDynamicOrderForm$(godaddyAppId[env]);
        }
        if (items.has(whitelabelDomainAppId[env])) {
          return this.orderFormStoreService.getDynamicOrderForm$(whitelabelDomainAppId[env]);
        }
        return observableOf([]);
      }),
    );

    // Prefill the order form with previous field values, whether a dynamic step or previously cached order forms
    const answers$ = combineLatest([resellerItemQuantityMap$, this.salesOrder$, dynamicFieldValues$]).pipe(
      map(([resellerItemQuantityMap, salesOrder, dynamicFieldValues]) => {
        const customFieldsAnswers = (salesOrder?.customFields || []).map((customField) => {
          const transformedCustomField = {
            productID: customField.addonKey?.appId || customField.productId,
            addonKey: customField.addonKey,
            customFieldsAnswers: customField.fields,
          } as CustomFieldsAnswers;

          // prioritize the field values set from the dynamic/custom order resellerItem stage upon multi-activation on single account
          const isDynamicForm =
            customField.productId === godaddyAppId[env] || customField.productId === whitelabelDomainAppId[env];
          if (isDynamicForm && dynamicFieldValues) {
            dynamicFieldValues.forEach((thisCustomFieldsAnswers) => {
              thisCustomFieldsAnswers.customFieldsAnswers.forEach((fieldAnswer) => {
                transformedCustomField.customFieldsAnswers.push({
                  answer: fieldAnswer.answer,
                  fieldId: fieldAnswer.fieldId,
                });
              });
            });
          }

          customField.fields.forEach((field: Field) => {
            // TODO allow a particular field of the order form component to be disabled/readonly
            // GoDaddy/Domains uses dynamic cached values for the domain field
            if (!(field.fieldId === 'domain' && isDynamicForm && dynamicFieldValues)) {
              transformedCustomField.customFieldsAnswers.push({
                answer: field.answer,
                fieldId: field.fieldId,
              });
            }
          });

          return transformedCustomField;
        });

        // if there was no cached order form, look for the dynamic values in cache
        const dynamicStageValue =
          resellerItemQuantityMap.get(godaddyAppId[env]) || resellerItemQuantityMap.get(whitelabelDomainAppId[env]);
        if (dynamicStageValue && dynamicFieldValues) {
          const answers: CustomFieldsAnswers = {
            productID: dynamicStageValue.appId,
            customFieldsAnswers: [],
          };
          dynamicFieldValues.forEach((thisCustomFieldsAnswers) => {
            thisCustomFieldsAnswers.customFieldsAnswers.forEach((fieldAnswer) => {
              answers.customFieldsAnswers.push({
                answer: fieldAnswer.answer,
                fieldId: fieldAnswer.fieldId,
                displayOnly: fieldAnswer.fieldId === 'domain',
              });
            });
          });
          customFieldsAnswers.push(answers);
        }

        return customFieldsAnswers;
      }),
    );

    this.includedApps$ = combineLatest([
      this.resellerItemService.getAppsForSelectedMarket(),
      this.resellerItemService.getCustomProductData(),
      this.appQuantityMap$,
    ]).pipe(
      take(1),
      map(([apps, customApps, appQuantityMap]) => {
        this.verboseAppQuantityMap = new Map(appQuantityMap);
        const customProductsAndApps = ResellerItemService.combineCustomProductsAndApps(customApps, apps);
        return this.filterForAppsGivenMap(customProductsAndApps, appQuantityMap);
      }),
    );

    this.includedAddons$ = combineLatest([
      this.resellerItemService.getAddonData(),
      this.addonQuantityMap$,
      this.appQuantityMap$,
    ]).pipe(
      take(1),
      map(([addons, addonQuantityMap, appQuantityMap]) => {
        this.verboseAppQuantityMap = new Map(appQuantityMap);
        return this.filterForAddons(addons, addonQuantityMap);
      }),
    );

    this.lineItems$ = this.salesOrder$.pipe(
      filter((order) => Boolean(order)),
      take(1),
      map((order) => {
        return order.lineItems;
      }),
    );

    this.mpOrderForms$ = combineLatest([this.includedApps$, this.includedAddons$]).pipe(
      take(1),
      switchMap(([includedApps, includedAddons]) => {
        return this.getMarketplaceOrderForms(this.partnerId, includedApps, includedAddons);
      }),
    );

    // grabs app info for billing info and creating resellerItems for activation
    const commonData$ = combineLatest([
      this.resellerItemService.getAddonData(),
      this.resellerItemService.getAppsForSelectedMarket(),
      this.resellerItemService.getCustomProductData(),
      this.mpOrderForms$,
      this.appQuantityMap$,
      this.addonQuantityMap$,
      this.partnerId$,
      this.accountService.users$,
    ]).pipe(
      catchError(() => {
        this.failureDialog([], 'unknownFailure');
        return observableOf(null);
      }),
      switchMap(([allAddons, allApps, allCPs, orderForms, appQuantityMap, addonQuantityMap, partnerId, users]) => {
        const appsAndCustomProducts = ResellerItemService.combineCustomProductsAndApps(allCPs, allApps);
        const addons = this.filterForAddons(allAddons, addonQuantityMap);
        const verboseApps = this.filterForVerboseApps(appsAndCustomProducts);

        this.populateResellerItemInfo(verboseApps, addons, appQuantityMap, users, orderForms);
        this.pushBillingIdsToInstanceSkuList(addons);
        this.pushBillingIdsToInstanceSkuList(verboseApps);

        if (this.skus && this.skus.length > 0) {
          this.subscriptions.push(
            this.pricingService.getMultiProductPricing(partnerId, this.skus).subscribe((results) => {
              if (results) {
                for (const sku of this.skus) {
                  if (
                    results[sku] &&
                    DeprecatedStrategy.fromBillingSDK(results[sku].strategy).isNonInvoicedStrategy()
                  ) {
                    this.hasInstantBillItems = true;
                    this.instantBillItemsSkus.push(sku);
                  }
                }
              }
            }),
          );
        }

        return this.formService.getCommonFieldValues().pipe(
          catchError(() => {
            this.failureDialog([], 'unknownFailure');
            return observableOf(null);
          }),
        );
      }),
      shareReplay(1),
    );

    this.orderFormAndPrice$ = combineLatest([this.hidePricingGuard.canActivate(), commonData$]).pipe(
      skipWhile(() => {
        return this.checkoutItems ? this.checkoutItems.length <= 0 : !this.checkoutItems;
      }),
      switchMap(([hidePricing, _]) => {
        this.hidePricing = hidePricing;
        return observableForkJoin(
          this.checkoutItems.map((item) => {
            if (!hidePricing && !item.appId.startsWith('CP-')) {
              const prevPurchaseCost = this.purchaseCosts.get(item.billingId);
              if (prevPurchaseCost) {
                return observableOf(prevPurchaseCost);
              }
              return this.billingApiService.getPurchaseCost(this.partnerId, item.billingId, item.quantity).pipe(
                map((price) => {
                  if (price) {
                    this.purchaseCosts.set(item.billingId, {
                      orderForm: item,
                      price: price,
                    });
                    return { orderForm: item, price: price };
                  }
                  const freq = item.billingFrequency === 'once' ? 'one time' : item.billingFrequency;
                  return {
                    orderForm: item,
                    price: {
                      billingFrequency: freq.toLowerCase(),
                      totalCost: -1,
                      currency: item.currency,
                    },
                  };
                }),
              );
            } else {
              const freq = item.billingFrequency === 'once' ? 'one time' : item.billingFrequency;
              const price = {
                billingFrequency: freq.toLowerCase(),
                totalCost: item.price * item.quantity,
                currency: item.currency,
              };
              return observableOf({ orderForm: item, price: price });
            }
          }),
        ).pipe(
          catchError(() => {
            this.failureDialog([], 'unknownFailure');
            return observableOf(null);
          }),
        );
      }),
      tap((orderPrice) => (this.hasItemsThatAreNotFree = orderPrice.some((order) => order.price.totalCost > 0))),
      shareReplay(1),
    );

    this.subscriptions.push(
      this.orderFormAndPrice$.subscribe((orderFormAndPrices) => {
        if (orderFormAndPrices) {
          orderFormAndPrices.forEach((orderFormAndPrice) => {
            if (orderFormAndPrice.price && orderFormAndPrice.price.totalCost === -1) {
              this.hasContactSale = true;
              this.contactSalesSkus.push(orderFormAndPrice.orderForm.billingId);
            }
          });
        }
      }),
    );

    const orderForms$ = combineLatest([this.mpOrderForms$, this.includedApps$, this.includedAddons$]).pipe(
      take(1),
      map(([mpOrderForms, apps, addons]) => {
        const items = apps.concat(addons);
        return items.reduce((forms, item) => {
          let commonFormRequiredFields: IncludedCommonFormFieldsInterface = {};
          let customFields: OrderFormFieldOptionsType[] = [];

          // check if the app has a custom entry URL in order to append it as a custom field
          if (item.usesActivationSpecificEntryUrl) {
            customFields.unshift(
              ProductOrderFormService.buildCustomEntryUrlForOrderForm(item.appId) as OrderFormFieldOptionsType,
            );
          }

          const mainId = item.addonId ? item.addonId : item.appId;
          const mpOrderForm = mpOrderForms.find((o) => o.appId === mainId);

          if (!!mpOrderForm || item.usesActivationSpecificEntryUrl) {
            if (!!mpOrderForm && mpOrderForm.orderForm) {
              if (mpOrderForm.orderForm.commonFormRequiredFields) {
                commonFormRequiredFields = mpOrderForm.orderForm.commonFormRequiredFields;
              }
              if (mpOrderForm.orderForm.orderForm && item.usesCustomizationForm) {
                customFields = customFields.concat(
                  mpOrderForm.orderForm.orderForm.map(
                    (f) =>
                      ({
                        ...f,
                        options: f.optionsWithLabels,
                      }) as OrderFormFieldOptionsType,
                  ),
                );
              }
            }

            const createdForm: OrderFormInterface = {
              appId: item.appId,
              addonId: item.addonId,
              customFields: customFields,
              commonFormRequiredFields: commonFormRequiredFields,
            };
            forms.push(createdForm);
            if (item.addonId && mpOrderForm && mpOrderForm.orderForm && mpOrderForm.orderForm.separateOrderForms) {
              for (let i = 1; i < item.quantity; i++) {
                forms.push(createdForm);
              }
            }
          }
          forms?.forEach((form) => (this.numCustomFormFields += form.customFields.length));
          return forms;
        }, []);
      }),
      shareReplay(1),
    );

    this.orderData$ = combineLatest([orderForms$, commonData$, answers$, this.accountService.users$]).pipe(
      take(1),
      tap(([orderForms]) => orderForms.forEach((form) => (this.numCustomFormFields += form.customFields.length))),
      map(([orderForms, commonData, answers, users]) => {
        return {
          orderForms: orderForms,
          commonData: commonData,
          answers: answers,
          options: ORDER_FORM_OPTIONS,
          users: users,
        };
      }),
      shareReplay(1),
    );

    this.productIds$ = this.getProductIds$();

    this.shouldDisplayBanner$ = orderForms$.pipe(map((orderForms) => orderForms?.length > 0));

    this.accountName$ = this.accountService.account$.pipe(map((account) => account?.company_name || ''));
    this.resellerName$ = this.app.config$.pipe(map((config) => config?.partnerName || ''));

    this.someProductHasWorkOrderEnabled$ = this.getProductIds$().pipe(
      switchMap((productIds) => {
        const reqs = productIds.map((appId) => {
          return this.formConfigService.getFulfillmentOrderConfig(
            new GetFulfillmentOrderConfigRequest({
              appId: appId,
              release: ReleaseType.RELEASE_TYPE_PUBLISHED,
              projectionFilter: { includeFormConfig: true },
            }),
          );
        });
        return forkJoin(reqs);
      }),
      map((resps) => {
        return resps.some((resp) => resp?.config?.isEnabled || resp?.config?.usesFulfillmentForm);
      }),
      tap((someProductHasWorkOrderEnabled) => {
        if (someProductHasWorkOrderEnabled) {
          this.productAnalyticsService.trackEvent(
            'order-form-product-uses-fulfillment-form',
            'work-order',
            'page-view',
          );
        }
      }),
    );
  }

  private getMarketplaceOrderForms(
    partnerId: string,
    apps: ResellerItem[],
    addons: ResellerItem[],
  ): Observable<MPOrderFormContainerInterface[]> {
    let appAndAddonIds = apps.filter((app) => app.usesCustomizationForm && app.orderForm).map((app) => app.appId);
    appAndAddonIds = appAndAddonIds.concat(
      addons.filter((addon) => addon.usesCustomizationForm && addon.orderForm).map((addon) => addon.addonId),
    );
    if (appAndAddonIds.length > 0) {
      return this.appService.getMultiOrderForms({ partnerId: partnerId, appIds: appAndAddonIds }).pipe(
        take(1),
        map((resp) => resp.orderFormContainer || []),
      );
    }
    return observableOf([]);
  }

  private pushBillingIdsToInstanceSkuList(resellerItems: ResellerItem[]): void {
    if (resellerItems && resellerItems.length > 0) {
      this.skus.push(...resellerItems.map((item) => item.billingId));
    }
  }

  private hasCustomOrCommonFields(orderForms: ResellerItemOrderForm): boolean {
    return (
      (orderForms.customFields && orderForms.customFields.length > 0) ||
      (orderForms.commonFields && orderForms.commonFields.length > 0)
    );
  }

  private initializeInstanceVariables(): void {
    this.checkoutItems = [];
    this.activationsRequiresApproval = [];
    this.appCheckoutItems = [];
    this.addonCheckoutItems = [];
    this.skus = [];
    this.instantBillItemsSkus = [];
    this.contactSalesSkus = [];
    this.productInfo = [];
  }

  // populates the product info for the order form component and prepares the checkout items for activation
  private populateResellerItemInfo(
    products: ResellerItem[],
    addons: ResellerItem[],
    appIdQMap: Map<string, number>,
    users: User[],
    orderForms: MPOrderFormContainerInterface[],
  ): void {
    this.initializeInstanceVariables();

    products.map((product) => {
      const combinedAddonResellerInfo: ResellerItemOrderForm[] = [];

      // build ResellerItem information for addons
      this.addonCheckoutItems = addons.reduce((addonCheckoutItems, addon) => {
        addon.currency = product.currency;
        if (addon.appId === product.appId) {
          const addonOrderForm = orderForms.find((o) => o.appId === addon.addonId);
          const addonResellerInfo: ResellerItemOrderForm = this.formService.createResellerItem(
            addon,
            users,
            addonOrderForm,
          );
          combinedAddonResellerInfo.push(addonResellerInfo);

          if (this.hasCustomOrCommonFields(addonResellerInfo)) {
            if (
              addonResellerInfo.quantity > 1 &&
              addonResellerInfo.orderForm &&
              addonResellerInfo.orderForm.separateOrderForms
            ) {
              for (let i = 0; i < addonResellerInfo.quantity; i++) {
                const separateAddonForm = this.formService.createResellerItem(addon, users, addonOrderForm);
                separateAddonForm.quantity = 1;
                addonCheckoutItems.push(separateAddonForm);
              }
            } else {
              addonCheckoutItems.push(addonResellerInfo);
            }
          } else {
            addonCheckoutItems.push(addonResellerInfo);
          }
          if (
            addon.activationRequiresApproval &&
            this.activationsRequiresApproval.indexOf(addonResellerInfo.name) < 0
          ) {
            this.activationsRequiresApproval.push(addon.name);
          }
        }
        return addonCheckoutItems;
      }, this.addonCheckoutItems);

      // handle the product's ResellerItem information
      const productOrderForm = orderForms.find((o) => o.appId === product.appId);
      const productResellerInfo: ResellerItemOrderForm = this.formService.createResellerItem(
        product,
        users,
        productOrderForm,
      );
      if (appIdQMap.has(product.appId)) {
        this.appCheckoutItems.push(productResellerInfo);
        this.checkoutItems.push(productResellerInfo);

        if (product.activationRequiresApproval) {
          this.activationsRequiresApproval.push(product.name);
        }
      }

      this.checkoutItems = this.checkoutItems.concat(combinedAddonResellerInfo);
    });

    // build the product info for each item being checked out, using only addons if they have an orderform
    this.checkoutItems.forEach((item) => {
      if (item.addonId) {
        const parentProductInfo = this.productInfo.find((product) => product.productId === item.appId);

        if (parentProductInfo) {
          // this case is to match an addon with a parent app
          parentProductInfo.addonInfo.push({
            addonId: item.addonId,
            name: item.name,
          });
        } else {
          // this case is to create addon product info that does not have a parent app being activated
          const parentApp = products.find((product) => product.appId === item.appId);
          this.productInfo.push({
            productId: item.appId,
            name: parentApp ? parentApp.name : '',
            tagline: parentApp ? parentApp.tagline : '',
            icon: parentApp ? parentApp.icon : '',
            addonInfo: [
              {
                addonId: item.addonId,
                name: item.name,
              },
            ],
          });
        }
      } else {
        // this case is for an app with no addon
        this.productInfo.push({
          productId: item.appId,
          name: item.name,
          tagline: item.tagline,
          icon: item.icon,
          addonInfo: [],
        });
      }
    });
  }

  private setAsTouched(group: UntypedFormGroup): void {
    group.markAsTouched();
    const controls = group.controls;
    for (const i in controls) {
      if (controls[i] instanceof UntypedFormControl) {
        controls[i].markAsTouched();
      } else if (controls[i] instanceof UntypedFormGroup) {
        this.setAsTouched(controls[i] as UntypedFormGroup);
      }
    }
    group.updateValueAndValidity({ onlySelf: true, emitEvent: true });
  }

  validateBillingOrder(
    instantBillItemSkus: string[],
    contactSalesSkus: string[],
    orderItems: ResellerItemOrderForm[],
  ): OrderFormSubmissionError | undefined {
    const hasInstantBillItems = instantBillItemSkus && instantBillItemSkus.length > 0;
    const hasContactSales = contactSalesSkus && contactSalesSkus.length > 0;
    if (hasInstantBillItems && hasContactSales) {
      const resultSkus = instantBillItemSkus.filter((x) => {
        return contactSalesSkus.indexOf(x) !== -1;
      });
      const uniqueResultSkus = Array.from(new Set(resultSkus));
      if (uniqueResultSkus.length > 0) {
        let names = [];
        uniqueResultSkus.forEach((sku) => {
          (orderItems || []).forEach((orderItem) => {
            if (orderItem.billingId === sku) {
              names.push(orderItem.name);
            }
          });
        });
        names = this.getFailedAppsDisplayContent(names);
        return {
          appNames: names,
          type: 'activationFailure',
          errorMessage:
            'These product(s) are not available for purchase at your current subscription tier. Please contact sales for more information.',
          openFailureDialog: true,
        };
      }
    }
  }

  validateForm(form: UntypedFormGroup): OrderFormSubmissionError | undefined {
    if (!form.valid) {
      this.setAsTouched(form);
      return {
        appNames: [],
        type: 'invalidForm',
        errorMessage: 'some order form fields are invalid',
        openFailureDialog: false,
      };
    }
  }

  validate(): OrderFormSubmissionError | undefined {
    let err = this.validateBillingOrder(this.instantBillItemsSkus, this.contactSalesSkus, this.checkoutItems);
    if (err) {
      return err;
    }
    err = this.validateForm(this.form);
    if (err) {
      return err;
    }
    return undefined;
  }

  applyCustomEntryUrlIfFieldExists(orders: ResellerItemOrderForm[]): ResellerItemOrderForm[] {
    return orders.map((order) => {
      if (!order.isCustom) {
        return order;
      }
      const customEntryUrl = this.formService.getCustomProductEntryURl(order);
      if (customEntryUrl) {
        return { ...order, entryUrl: customEntryUrl };
      }
      return order;
    });
  }

  private cacheSalesOrder(): void {
    this.salesOrder$.pipe(take(1)).subscribe((order) => {
      this.orderFormStoreService.cacheOrder(
        new Order({
          ...order,
          customFields: this.salesOrderFormComponent.getCustomFormData(),
          commonFields: this.salesOrderFormComponent.getCommonFormData(),
          extraFields: this.salesOrderFormComponent.getExtraFieldsData(),
        }),
      );
      this.salesOrderCached$$.next(true);
    });
  }

  // Caches the reseller items with order form values so they can be read at other stages of activation
  cacheOrderForms(): void {
    this.checkoutItems.forEach((section: ResellerItemOrderForm) => {
      const prefillSectionKey = JSON.stringify([this.accountGroupId, section.addonId || section.appId]);
      window.sessionStorage.removeItem(prefillSectionKey);

      // remove the dynamic session item as its now saved on the cached app
      const dynamicPrefillSectionKey = ProductOrderFormStoreService.buildKeyForDynamicOrderForm(
        this.accountGroupId,
        section.appId,
      );
      window.sessionStorage.removeItem(dynamicPrefillSectionKey);
    });
  }

  onSubmit(): void {
    if (this.isFormLoading) {
      return;
    }

    const err = !this.salesOrderFormComponent.validateForm();
    if (err) {
      this.snackbarService.errorSnack(this.translate.instant('SALES_ORDERS.ERRORS.FORM_IS_NOT_VALID'));
      return;
    }

    if (this.productActivationPrereqFormComponent.businessActivationForm.invalid) {
      this.snackbarService.errorSnack(this.translate.instant('SALES_ORDERS.ERRORS.PRE_REQ_FORM_IS_NOT_VALID'));
      return;
    }

    this.cacheOrderForms();
    this.cacheSalesOrder();
    this.salesOrderCached$$
      .pipe(
        skipWhile((ready) => !ready),
        take(1),
      )
      .subscribe(() => this.activationNavigationService.navigateToNextStep('order-form'));
  }

  private getFailedAppsDisplayContent(inputs: string[]): string[] {
    // In the array of failed activations, there might be multiple entry of an product/addon,
    // for example: ['Prod', 'Addon', 'Addon']. This function will transform the array to ['Prod', 'Addon (2)'],
    // so that the dialog can show the information in a more readable way.
    const resultMap: Map<string, number> = new Map<string, number>();
    const outputString: string[] = [];
    inputs.forEach((input) => {
      resultMap.set(input, (resultMap.get(input) || 0) + 1);
    });

    resultMap.forEach((num, name) => {
      const countString = num !== 1 ? ' (' + resultMap.get(name) + ')' : '';
      outputString.push(' ' + name + countString);
    });
    return outputString;
  }

  failureDialog(productNames: string[], type: OrderFormSubmissionErrorType, errorMessage?: string): void {
    if (this.vDialog) {
      // There's already a failure dialog open. Don't open another on top of it.
      return;
    }
    let title = '';
    let contentHtml = '';
    const isSuccess = false;
    const buttons = [];
    switch (type) {
      case 'activationFailure':
        title = 'Oops, there was a problem activating some products!';
        contentHtml =
          '<p> The following products failed to Activate: <b>' +
          productNames +
          '</b></p>' +
          '<p>' +
          errorMessage +
          '</p>';
        break;
      case 'unknownFailure':
        title = 'Oops, something unexpected happened!';
        contentHtml = '<p>Please return to the previous page and try again later.</p>';
        break;
      case 'noAppsError':
        title =
          'Oops, we could not load the order form! The cause could be that this account already has all of the products active';
        contentHtml =
          '<p>Please return to the previous page and try again.</p>' +
          '<p>' +
          'If the problem persists, contact your Partner Success Manager or partnersuccess@vendasta.com.' +
          '</p>';
        break;
    }
    const goBack: ButtonDescriptor = {
      title: 'Return to Account Details',
      action: 'goBack',
    };
    buttons.push(goBack);
    this.vDialog = this.dialog.open(VDialogComponent, new MatDialogConfig());
    this.vDialog.componentInstance.isSuccess = isSuccess;
    this.vDialog.componentInstance.title = title;
    this.vDialog.componentInstance.contentHtml = contentHtml;
    this.vDialog.componentInstance.buttons = buttons;
    this.vDialog.afterClosed().subscribe((result) => {
      if (result === 'goBack') {
        this.activationNavigationService.navigateToPreviousStep('order-form');
      }
    });
  }

  private getProductIds$(): Observable<string[]> {
    return this.salesOrder$.pipe(
      filter((order) => Boolean(order)),
      map((order) => {
        return order.lineItems.map((product) => product.appKey.appId);
      }),
      publishReplay(1),
      refCount(),
    );
  }

  updateFormLoadingStatus(loading: boolean): void {
    this.isFormLoading = loading;
  }

  goBack(): void {
    this.activationNavigationService.navigateToPreviousStep('order-form');
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.formService.destroyService();
  }
}
